class CartRewardsCalculator {
  constructor() {
    this.totalRewardsElement = null;
    this.isCalculating = false;
    this.lastCartHash = null;
    this.debounceTimer = null;
    this.init();
  }

  init() {
    this.findRewardsElement();
    this.setupRealTimeCartEvents();
    this.setupSectionReloadDetection();
    this.calculateInitialRewards();
  }

  // Detecta quando a section foi recarregada
  setupSectionReloadDetection() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          // Verifica se elementos de rewards foram adicionados/removidos
          const addedRewardsElements = Array.from(mutation.addedNodes).filter(
            (node) =>
              node.nodeType === 1 &&
              (node.classList?.contains("rewards-section") ||
                node.querySelector?.(".rewards-section"))
          );

          if (addedRewardsElements.length > 0) {
            this.findRewardsElement();
            setTimeout(() => this.calculateInitialRewards(), 100);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  findRewardsElement() {
    this.totalRewardsElement = document.querySelector(
      ".totals__total-value-rewards"
    );

    if (this.totalRewardsElement) {
      const isLoading = this.totalRewardsElement.dataset.loading === "true";

      if (isLoading) {
        delete this.totalRewardsElement.dataset.loading;
      }
    }
  }

  setupRealTimeCartEvents() {
    this.interceptCartAjaxCalls();

    document.addEventListener("cart:updated", (event) => {
      this.debouncedSync(event.detail);
    });

    document.addEventListener("cart:added", (event) => {
      this.debouncedSync();
    });

    document.addEventListener("cart:changed", (event) => {
      this.debouncedSync();
    });
  }

  debouncedSync(cartData = null) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.syncCartWithRewards(cartData);
    }, 300);
  }

  interceptCartAjaxCalls() {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const [url, options] = args;
      const response = await originalFetch(...args);

      if (this.isCartOperation(url)) {
        // Se for uma operação que vai recarregar a section
        if (url.includes("/cart/update") || url.includes("/cart/change")) {
          setTimeout(() => {
            this.findRewardsElement();
            this.debouncedSync();
          }, 500); // Aguarda mais tempo para section reload
        } else {
          this.debouncedSync();
        }
      }

      return response;
    };
  }

  isCartOperation(url) {
    return ["/cart/add", "/cart/update", "/cart/change", "/cart/clear"].some(
      (endpoint) => url.includes(endpoint)
    );
  }

  generateCartHash(cartData) {
    const items = cartData.items || [];
    return items.map((item) => `${item.variant_id}:${item.quantity}`).join("|");
  }

  async syncCartWithRewards(cartData = null) {
    if (this.isCalculating) {
      return;
    }

    try {
      this.isCalculating = true;

      // Busca dados do carrinho se necessário
      if (!cartData) {
        const response = await fetch("/cart.js");
        cartData = await response.json();
      }

      const currentHash = this.generateCartHash(cartData);

      // Verifica se carrinho realmente mudou
      if (currentHash === this.lastCartHash) {
        return;
      }

      this.lastCartHash = currentHash;
      const cartItems = cartData.items || [];

      if (cartItems.length === 0) {
        this.updateRewardsDisplay(0);
        this.hideAllItemRewards();
        return;
      }

      this.showLoadingState();

      // Verifica empresa
      const cartRewardsData = window.cartRewardsData;
      if (!cartRewardsData?.hasCompany || !cartRewardsData?.companyId) {
        this.updateRewardsDisplay(0);
        this.hideAllItemRewards();
        return;
      }

      // Prepara para API
      const apiItems = cartItems.map((item) => ({
        skuId: `gid://shopify/ProductVariant/${item.variant_id}`,
        quantity: item.quantity,
        stockLevelLimit: 999999999,
      }));

      // Chama API
      const response = await retrieveProductInfoByOrganization(
        cartRewardsData.companyId,
        apiItems
      );

      // Processa rewards totais e individuais
      const { totalRewards, itemRewards } = this.processRewardsResponse(
        response,
        cartItems
      );

      this.updateRewardsDisplay(totalRewards);
      this.updateItemRewards(itemRewards);
    } catch (error) {
      console.error("💥 Erro:", error);
      this.updateRewardsDisplay(0);
      this.hideAllItemRewards();
    } finally {
      this.isCalculating = false;
    }
  }

  processRewardsResponse(response, cartItems) {
    // Usa o totalRewards do summary se disponível
    let totalRewards = 0;
    if (response?.summary?.totalRewards) {
      totalRewards = parseInt(response.summary.totalRewards) || 0;
    }

    const itemRewards = new Map();

    if (response?.items) {
      response.items.forEach((apiItem) => {
        if (apiItem.rewards && apiItem.shopifySkuId) {
          const itemRewardsValue = parseInt(apiItem.rewards) || 0;
          const itemRewardsValueUnit = parseInt(apiItem.unitRewards) || 0;

          // Encontra o item do carrinho correspondente
          const cartItem = cartItems.find(
            (item) =>
              `gid://shopify/ProductVariant/${item.variant_id}` ===
              apiItem.shopifySkuId
          );

          if (cartItem && itemRewardsValue > 0) {
            const totalItemRewards = itemRewardsValue;

            // Se não temos totalRewards do summary, calcula manualmente
            if (!response?.summary?.totalRewards) {
              totalRewards += totalItemRewards;
            }

            itemRewards.set(cartItem.variant_id, {
              unitRewards: itemRewardsValue,
              totalRewards: totalItemRewards,
              quantity: cartItem.quantity,
            });
          }
        }
      });
    }
    return { totalRewards, itemRewards };
  }

  updateItemRewards(itemRewards) {
    // Primeiro, esconde todos os rewards de items E para a animação
    const allItemRewards = document.querySelectorAll(".cart-item__rewards");
    allItemRewards.forEach((element) => {
      element.style.display = "none";

      // Para a animação de loading nos ícones
      const icon = element.querySelector(".cart-item__rewards-icon");
      if (icon) {
        icon.classList.remove("loading-spin");
      }
    });

    // Depois, atualiza e mostra apenas os items com rewards
    itemRewards.forEach((rewardData, variantId) => {
      const itemRewardElement = document.querySelector(
        `#cart-item-rewards-${variantId}`
      );

      if (itemRewardElement) {
        const rewardsText = itemRewardElement.querySelector(
          ".cart-item__rewards-text"
        );

        if (rewardsText) {
          const formattedRewards =
            rewardData.totalRewards.toLocaleString("pt-BR");
          rewardsText.textContent = `ou ${formattedRewards} pts`;

          // Mostra o elemento
          itemRewardElement.style.display = "block";
        }
      } else {
        console.warn(
          `⚠️ Elemento de rewards não encontrado para item ${variantId}`
        );
      }
    });
  }

  hideAllItemRewards() {
    const allItemRewards = document.querySelectorAll(".cart-item__rewards");
    allItemRewards.forEach((element) => {
      element.style.display = "none";

      // Para a animação de loading nos ícones também
      const icon = element.querySelector(".cart-item__rewards-icon");
      if (icon) {
        icon.classList.remove("loading-spin");
      }
    });
  }

  calculateTotalRewards(response) {
    let total = 0;

    if (response?.items) {
      response.items.forEach((item) => {
        if (item.rewards) {
          total += parseInt(item.rewards) || 0;
        }
      });
    }

    return total;
  }

  async calculateInitialRewards() {
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));

      const response = await fetch("/cart.js");
      const cartData = await response.json();

      await this.syncCartWithRewards(cartData);
    } catch (error) {
      console.error("💥 Erro inicial:", error);
      this.updateRewardsDisplay(0);
      this.hideAllItemRewards();
    }
  }

  showLoadingState() {
    if (this.totalRewardsElement) {
      // Encontra ou cria o ícone girando
      let rewardsIcon = this.totalRewardsElement.querySelector(".rewards-icon");
      if (!rewardsIcon) {
        // Se não encontrar, cria um novo
        const iconSrc =
          document.querySelector("#rewards-icon-id")?.src ||
          `${
            window.Shopify?.shop
              ? `https://${window.Shopify.shop}/cdn/shop/files/icon-rewards-logo.svg`
              : "/assets/icon-rewards-logo.svg"
          }`;

        this.totalRewardsElement.innerHTML = `
          <img src="${iconSrc}" width="16" height="16" class="rewards-icon loading-spin"/>
        `;
      } else {
        // Se encontrar, apenas adiciona a classe de loading
        rewardsIcon.className = "rewards-icon loading-spin";
        // Remove qualquer texto que possa estar junto
        const textNodes = Array.from(
          this.totalRewardsElement.childNodes
        ).filter((node) => node.nodeType === Node.TEXT_NODE);
        textNodes.forEach((node) => node.remove());
      }
    }

    // Também mostra loading nos items individuais
    const allItemRewards = document.querySelectorAll(".cart-item__rewards");
    allItemRewards.forEach((element) => {
      const icon = element.querySelector(".cart-item__rewards-icon");
      if (icon) {
        icon.classList.add("loading-spin");
      }
    });
  }

  updateRewardsDisplay(totalRewards) {
    if (!this.totalRewardsElement) {
      console.warn("❌ Elemento não encontrado");
      return;
    }

    const rewardsSection = this.totalRewardsElement.closest(".rewards-section");

    if (totalRewards > 0) {
      const formatted = totalRewards.toLocaleString("pt-BR");

      const existingIcon =
        this.totalRewardsElement.querySelector(".rewards-icon");
      const iconSrc =
        existingIcon?.src ||
        document.querySelector("#rewards-icon-id")?.src ||
        `${
          window.Shopify?.shop
            ? `https://${window.Shopify.shop}/cdn/shop/files/icon-rewards-logo.svg`
            : "/assets/icon-rewards-logo.svg"
        }`;

      this.totalRewardsElement.innerHTML = `
        <span class="rewards-total-value">
          <img src="${iconSrc}" width="16" height="16" class="rewards-icon"/>
          ${formatted} pts
        </span>
      `;

      if (rewardsSection) {
        rewardsSection.style.display = "flex";
      }
    } else {
      if (rewardsSection) {
        rewardsSection.style.display = "none";
      }
    }
  }
}

// CSS
const style = document.createElement("style");
style.textContent = `
  .rewards-icon {
    width: 16px; 
    height: 16px;
    margin-right: 4px;
    vertical-align: middle;
  }

  .loading-spin {
    animation: rewards-spin 1s linear infinite;
  }
  
  @keyframes rewards-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .rewards-total-value {
    display: flex;
    align-items: center;
    font-weight: 500;
  }

  .cart-item__rewards-content {
    display: flex;
    align-items: center;
    font-size: 1.6rem;
    margin: 0;
    color: #000000;
    width: max-content;
  }

  .cart-item__rewards-icon {
    width: 12px !important;
    height: 12px !important;
    margin-right: 4px;
  }

  .cart-item__rewards-text {
    font-weight: 500;
  }
`;
document.head.appendChild(style);

let calculatorInstance = null;

document.addEventListener("DOMContentLoaded", () => {
  if (!calculatorInstance) {
    setTimeout(() => {
      calculatorInstance = new CartRewardsCalculator();
    }, 1000);
  }
});
